var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import { ARROW_DOWN_KEY, ARROW_LEFT_KEY, ARROW_RIGHT_KEY, ARROW_UP_KEY, END_KEY, ENTER_KEY, HOME_KEY, SPACE_KEY, } from '#constants';
import { C } from '#integrations';
import { Typography } from '../../../Typography';
import { useTabContext } from '../../context/useTabContext';
import { useTabListContext } from '../../context/useTabListContext';
import { TabsTestIdFactory } from '../../interactions';
import styles from './Tab.module.css';
const KIND_CLASS_MAP = {
    default: styles.Default,
    ghost: styles.Ghost,
};
const KIND_ACTIVE_CLASS_MAP = {
    default: styles.DefaultActive,
    ghost: styles.GhostActive,
};
const SIZE_CLASS_MAP = {
    default: undefined,
    small: styles.Small,
};
export const Tab = ({ children, EndIcon, StartIcon, tabId }) => {
    _react_refresh_temp_2();
    const { activeTabId, focusedTabId, setActiveTabId, setFocusedTabId } = useTabContext();
    const { isFullWidth, kind, orientation, registeredTabs, registerTab, tabSize, unregisterTab, } = useTabListContext();
    const tabElement = useRef(null);
    const isActive = activeTabId === tabId;
    const isFocused = focusedTabId === tabId;
    useEffect(() => {
        registerTab(tabId);
        return () => unregisterTab(tabId);
    }, [registerTab, tabId, unregisterTab]);
    useLayoutEffect(() => {
        if (isFocused) {
            tabElement.current?.focus();
        }
    }, [isFocused]);
    const onTabClick = useCallback(() => {
        if (!isActive) {
            setActiveTabId(tabId);
            setFocusedTabId(tabId);
        }
    }, [isActive, setActiveTabId, setFocusedTabId, tabId]);
    const { firstTabId, isFirst, isLast, lastTabId, nextTabId, prevTabId } = useMemo(() => {
        const tabsArray = [...registeredTabs];
        return {
            firstTabId: tabsArray[0],
            lastTabId: tabsArray[tabsArray.length - 1],
            isFirst: tabId === tabsArray[0],
            isLast: tabId === tabsArray[tabsArray.length - 1],
            nextTabId: tabsArray[tabsArray.indexOf(tabId) + 1],
            prevTabId: tabsArray[tabsArray.indexOf(tabId) - 1],
        };
    }, [registeredTabs, tabId]);
    const focusNext = useCallback(() => {
        if (isLast && firstTabId) {
            setFocusedTabId(firstTabId);
        }
        if (nextTabId) {
            setFocusedTabId(nextTabId);
        }
    }, [firstTabId, isLast, nextTabId, setFocusedTabId]);
    const focusPrev = useCallback(() => {
        if (isFirst && lastTabId) {
            setFocusedTabId(lastTabId);
        }
        if (prevTabId) {
            setFocusedTabId(prevTabId);
        }
    }, [isFirst, lastTabId, prevTabId, setFocusedTabId]);
    const onKeyDown = (event) => {
        const { key } = event;
        if (/Arrow|Home|End/.test(key)) {
            event.preventDefault();
        }
        switch (key) {
            case ENTER_KEY:
            case SPACE_KEY:
                onTabClick();
                break;
            case HOME_KEY:
                if (firstTabId) {
                    setFocusedTabId(firstTabId);
                }
                break;
            case END_KEY:
                if (lastTabId) {
                    setFocusedTabId(lastTabId);
                }
                break;
            case ARROW_RIGHT_KEY:
                if (orientation === 'horizontal') {
                    focusNext();
                }
                break;
            case ARROW_DOWN_KEY:
                if (orientation === 'vertical') {
                    focusNext();
                }
                break;
            case ARROW_LEFT_KEY:
                if (orientation === 'horizontal') {
                    focusPrev();
                }
                break;
            case ARROW_UP_KEY:
                if (orientation === 'vertical') {
                    focusPrev();
                }
                break;
        }
    };
    return (_jsxs("button", { "aria-controls": `panel-${tabId}`, "aria-selected": isActive, className: C(styles.Tab, isActive && styles.Active, isActive && KIND_ACTIVE_CLASS_MAP[kind], SIZE_CLASS_MAP[tabSize], KIND_CLASS_MAP[kind], isFullWidth && styles.FullWidth), "data-testid": TabsTestIdFactory.getTabTestId(tabId), id: `tab-${tabId}`, onClick: onTabClick, onKeyDown: onKeyDown, ref: tabElement, role: "tab", tabIndex: isActive || isFocused ? 0 : -1, type: "button", children: [StartIcon && _jsx(StartIcon, { size: "xsmall" }), _jsx(Typography, { className: styles.Text, "data-testid": TabsTestIdFactory.getTabTextTestId(tabId), kind: "generic-small", children: children }), EndIcon && _jsx(EndIcon, {})] }));
};
_react_refresh_temp_1 = Tab;
$RefreshReg$(_react_refresh_temp_1, "Tab");
_react_refresh_temp_2(Tab, "BmUs3JKwZwkFsO5OCgmNswCiRBc=", false, () => [useTabContext, useTabListContext]);
